// src/components/NotificationsSection.js - DESIGN COMPACT ET MINIMALISTE

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, ListGroup, Badge } from 'react-bootstrap';
import { 
    FaBell, FaGift, FaRocket, FaCheckCircle, FaTimesCircle, FaChevronRight,
    FaRegEnvelopeOpen, FaEnvelope, FaExclamationCircle // Ajout d'icônes potentielles pour la compaction
} from 'react-icons/fa'; 
import { useSocket } from '../SocketContext'; 

// --- Fonctions utilitaires (Ajustées pour un style plus compact) ---
const getNotificationStyle = (notif) => {
    const type = notif.type || '';
    const message = notif.message || '';
    
    // Priorité aux types spécifiques
    if (type.includes('messageReply')) return { icon: <FaEnvelope />, color: 'text-primary' }; // Icône message
    if (type.includes('withdrawal_approved') || message.includes('gain double')) return { icon: <FaCheckCircle />, color: 'text-success' };
    if (type.includes('withdrawal_rejected')) return { icon: <FaTimesCircle />, color: 'text-danger' };
    
    // Logique générale par mots-clés
    if (message.includes('Félicitations') || message.includes('succès')) return { icon: <FaGift />, color: 'text-info' };
    if (message.includes('créé avec succès') || message.includes('actif')) return { icon: <FaRocket />, color: 'text-info' };
    if (message.includes('Erreur') || message.includes('problème')) return { icon: <FaExclamationCircle />, color: 'text-danger' }; // Icône d'erreur
    
    // Défaut
    return { icon: <FaBell />, color: 'text-secondary' };
};

const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "À l'instant";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`; // Plus compact : 'm' pour minutes
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`; // Plus compact : 'h' pour heures
    // Pour plus ancien, juste le jour et le mois (ex: 15 sept)
    return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
};

// --- Composant principal ---

const NotificationsSection = () => {
    const { notifications, unreadCount, markAsRead } = useSocket(); 
    
    // Limite l'affichage aux 5 dernières notifications pour le dashboard
    const displayNotifications = notifications ? notifications.slice(0, 5) : [];
    
    const handleNotificationClick = (notifId) => {
        if (typeof markAsRead === 'function') {
            markAsRead(notifId);
        } else {
            console.warn("markAsRead n'a pas pu être appelée. Le SocketContext est-il correctement configuré ?");
        }
    };
    
    return (
        <Card className="shadow-sm h-100 border-0">
            {/* EN-TÊTE : plus compact, icône plus petite, texte réduit */}
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center p-2"> 
                <h6 className="mb-0 text-dark fw-bold small"> {/* h6 et 'small' pour compacter le titre */}
                    <FaBell className="me-2 text-primary" style={{ fontSize: '0.9rem' }} /> {/* Icône plus petite */}
                    Activité Récente 
                    {unreadCount > 0 && <Badge bg="danger" className="ms-2 rounded-pill small">{unreadCount}</Badge>} {/* Badge plus petit */}
                </h6>
            </Card.Header>
            
            <Card.Body className="p-0">
                {displayNotifications.length === 0 ? (
                    <div className="p-3 text-center text-muted small"> {/* padding réduit, texte plus petit */}
                        <FaRegEnvelopeOpen className="mb-2 fs-5" /> {/* Icône plus petite */}
                        <p className="mb-0">Aucune alerte récente.</p> {/* Message plus court */}
                    </div>
                ) : (
                    <ListGroup variant="flush">
                        {displayNotifications.map((notif) => {
                            const { icon, color } = getNotificationStyle(notif);
                            const isUnread = !notif.read;

                            return (
                                <ListGroup.Item 
                                    key={notif._id} 
                                    action
                                    className={`d-flex align-items-center py-2 px-3 ${isUnread ? 'bg-light' : ''}`} // Padding réduit, pas de bordure latérale par défaut
                                    style={{ cursor: 'pointer', borderLeft: isUnread ? '4px solid var(--bs-primary)' : 'none' }} // Bordure conditionnelle via style
                                    onClick={() => handleNotificationClick(notif._id)} 
                                >
                                    <div className={`me-2 ${color}`} style={{ fontSize: '0.9rem' }}>{icon}</div> {/* Icône plus petite */}
                                    <div className="flex-grow-1 text-truncate">
                                        <div className={`fw-semibold small ${isUnread ? 'text-dark' : 'text-muted'}`}> {/* Texte plus petit */}
                                            {notif.message.substring(0, 40)} {/* Message encore plus court */}
                                            {notif.message.length > 40 && '...'}
                                        </div>
                                        <small className="text-secondary d-block mt-0" style={{ fontSize: '0.75rem' }}> {/* Texte heure encore plus petit */}
                                            {formatTimeAgo(notif.createdAt)}
                                        </small>
                                    </div>
                                    <FaChevronRight className="text-muted ms-2" style={{ fontSize: '0.6rem' }} /> {/* Icône flèche plus petite */}
                                </ListGroup.Item>
                            );
                        })}
                    </ListGroup>
                )}
            </Card.Body>

            {/* PIED DE PAGE : texte plus petit, padding réduit */}
            <Card.Footer className="text-center bg-light border-top p-2"> 
                <Link to="/notifications" className="text-primary fw-bold smaller text-decoration-none"> {/* 'smaller' pour un texte vraiment petit */}
                    Voir toutes les alertes <FaChevronRight className="ms-1" style={{ fontSize: '0.6rem' }} />
                </Link>
            </Card.Footer>
        </Card>
    );
};

export default NotificationsSection;